import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { recruiterAPI } from '@/services/api';
import { Users, Download, CheckCircle, Clock, ArrowLeft, Trophy, Medal, MapPin } from 'lucide-react';

export default function RecruiterJourneyView() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('match');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/jobs/${jobId}/journey?sort_by=${sortBy}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        setData(await r.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    if (jobId) load();
  }, [jobId, sortBy]);

  const downloadResume = async (candidateId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/download-resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ candidateId, format: 'classic' })
      });
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidate_resume.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  if (loading) return <p className="text-slate-500 dark:text-zinc-500">Loading...</p>;
  if (!data) return <p className="text-red-600 dark:text-red-400">Failed to load</p>;

  const rankBadge = (i) => {
    if (i === 0) return <Trophy className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
    if (i === 1) return <Medal className="w-4 h-4 text-slate-600 dark:text-zinc-300" />;
    if (i === 2) return <Medal className="w-4 h-4 text-amber-600" />;
    return null;
  };

  return (
    <div data-testid="recruiter-journey-view">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">{data.job_title} — Candidates</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-4">{data.total} candidates in pipeline</p>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-slate-500 dark:text-zinc-500">Sort by:</span>
        <button onClick={() => setSortBy('match')} className={`px-3 py-1 rounded text-xs ${sortBy === 'match' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>Match %</button>
        <button onClick={() => setSortBy('jf')} className={`px-3 py-1 rounded text-xs ${sortBy === 'jf' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`} data-testid="sort-jf">JF Score</button>
      </div>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-200 dark:border-zinc-800/50">
              <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">#</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Candidate</th>
              <th className="text-center px-3 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Matched</th>
              <th className="text-center px-3 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Invited</th>
              <th className="text-center px-3 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Resume Matched</th>
              <th className="text-center px-3 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">JF%</th>
              <th className="text-right px-4 py-3 text-xs text-slate-500 dark:text-zinc-500 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.candidates?.map((c, i) => (
              <tr key={c.match_id} className="border-b border-slate-200 dark:border-zinc-800/30 last:border-0 hover:bg-slate-100 dark:hover:bg-zinc-800/20" data-testid={`journey-row-${c.match_id}`}>
                <td className="px-4 py-3">{rankBadge(i)}</td>
                <td className="px-4 py-3">
                  <p className="text-slate-600 dark:text-zinc-300 text-sm font-medium">{c.name}</p>
                  <p className="text-slate-500 dark:text-zinc-600 text-xs">{c.email}</p>
                </td>
                <td className="text-center px-3 py-3">{c.matched ? <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mx-auto" /> : <span className="text-slate-600 dark:text-zinc-700">—</span>}</td>
                <td className="text-center px-3 py-3">{c.invited ? <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mx-auto" /> : <Clock className="w-4 h-4 text-slate-500 dark:text-zinc-600 mx-auto" />}</td>
                <td className="text-center px-3 py-3">{c.screened ? <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mx-auto" /> : <span className="text-slate-600 dark:text-zinc-700">—</span>}</td>
                <td className="text-center px-3 py-3 text-sm font-medium">
                  {c.jf_score != null ? <span className={c.jf_score >= 70 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>{c.jf_score}%</span> : <span className="text-slate-600 dark:text-zinc-700">—</span>}
                </td>
                <td className="text-right px-4 py-3">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => navigate(`/recruiter/jobs/${jobId}/journey/${c.match_id}`)} className="text-xs text-emerald-700 dark:text-emerald-300 hover:text-emerald-200 flex items-center gap-1" data-testid={`open-journey-${c.match_id}`}>
                      <MapPin className="w-3 h-3" /> Journey
                    </button>
                    <button onClick={() => downloadResume(c.applicant_id)} className="text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1" data-testid={`download-${c.match_id}`}>
                      <Download className="w-3 h-3" /> Resume
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
